// src/pages/ProgramDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { 
  FaHome, 
  FaChevronRight, 
  FaCalendarAlt, 
  FaClock, 
  FaUsers, 
  FaStar, 
  FaCheckCircle, 
  FaArrowRight,
  FaAngleDown,
  FaAngleUp,
  FaArrowLeft
} from 'react-icons/fa';
import Section from '../components/common/Section';
import Heading from '../components/common/Heading';
import Button from '../components/common/Button';
import ProgramDetails from '../components/programs/ProgramDetails';
import COURSES_DATA from '../data/coursesdata';

const ProgramDetailPage = () => {
  const { programId } = useParams();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [openAccordion, setOpenAccordion] = useState(null);
  
  useEffect(() => {
    // Set loading state
    setLoading(true);
    
    // Find all courses from all categories
    const allCourses = Object.values(COURSES_DATA).flatMap(category => category.courses);
    
    // Find the program by ID
    const foundProgram = allCourses.find(c => c.id === programId);
    
    if (foundProgram) {
      // Get related programs from the same category
      const allPrograms = Object.values(COURSES_DATA).flatMap(category => category.courses);
      const relatedPrograms = allPrograms
        .filter(p => p.category === foundProgram.category && p.id !== programId)
        .slice(0, 2);
      
      // Add standard program details for any missing fields
      const enrichedProgram = {
        ...foundProgram,
        duration: foundProgram.duration || '12 weeks (part-time)',
        nextCohortStart: foundProgram.nextCohortStart || 'Contact Afrovate Tech Hub',
        schedule: foundProgram.schedule || 'Weekends (Saturdays & Sundays, 9am-1pm)',
        price: foundProgram.price || 'Contact Afrovate Tech Hub',
        enrollmentStatus: foundProgram.enrollmentStatus || 'Open for registration',
        instructors: [
          {
            name: 'Ing. Alfred Mbayoh',
            title: 'Lead Instructor',
            image: '/images/team/alfred_mbayoh.jpg',
            fallbackImage: 'https://placehold.co/100x100/1a237e/ffffff?text=AM'
          }
        ],
        // Use the curriculum from coursesdata.js if available, otherwise use default
        curriculum: foundProgram.curriculum || [
          {
            id: 'module1',
            title: 'Module 1: Foundations & Basics',
            duration: '3 weeks',
            lessons: [
              'Introduction to software/tools and installation',
              'User interface navigation and settings',
              'Basic drawing tools and commands',
              'Project setup and standards'
            ]
          },
          {
            id: 'module2',
            title: 'Module 2: Intermediate Techniques',
            duration: '4 weeks',
            lessons: [
              'Advanced modeling and design techniques',
              'Material application and rendering',
              'Component libraries and custom elements',
              'Documentation and annotation'
            ]
          },
          {
            id: 'module3',
            title: 'Module 3: Advanced Projects',
            duration: '4 weeks',
            lessons: [
              'Real-world project implementation',
              'Collaborative workflows',
              'Quality control and checking',
              'Final portfolio project'
            ]
          },
          {
            id: 'module4',
            title: 'Module 4: Industry Application',
            duration: '1 week',
            lessons: [
              'Field trip to real project sites',
              'Industry best practices',
              'Job seeking and freelancing strategies',
              'Certification preparation'
            ]
          }
        ],
        // Add more default fields
        faq: [
          {
            id: 'faq1',
            question: 'Are there any prerequisites for this program?',
            answer: foundProgram.prerequisites 
                  ? foundProgram.prerequisites.join(' ') 
                  : 'There are no strict prerequisites, but basic computer literacy is required. For engineering-related programs, having a background in STEM fields is beneficial but not mandatory.'
          },
          {
            id: 'faq2',
            question: 'What equipment do I need for the program?',
            answer: 'You will need a laptop capable of running the required software. We have computer labs available for those who don\'t have access to suitable equipment.'
          },
          {
            id: 'faq3',
            question: 'How are the classes conducted?',
            answer: 'Classes are conducted in-person at our facility in Freetown. We follow a practical, hands-on approach with minimal theory and maximum practice time.'
          },
          {
            id: 'faq4',
            question: 'Will I receive a certificate upon completion?',
            answer: 'Yes, all participants who successfully complete the program will receive an Afrovate Tech Hub certification. Our certificates are recognized by many industry partners in Sierra Leone.'
          },
          {
            id: 'faq5',
            question: 'Are there payment plans available?',
            answer: 'Yes, we offer flexible payment plans. You can pay in installments over the duration of the program. We also offer scholarships for exceptionally talented students with financial constraints.'
          }
        ],
        relatedPrograms
      };
      
      setProgram(enrichedProgram);
    }
    
    // Simulate API delay
    setTimeout(() => {
      setLoading(false);
    }, 500);
    
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, [programId]);
  
  // Toggle accordion sections
  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };
  
  // Render loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Handle program not found
  if (!program) {
    return (
      <Section background="white" padding="lg" className="pt-32">
        <div className="text-center">
          <Heading level={2}>Program Not Found</Heading>
          <p className="text-gray-600 mb-6">The program you're looking for doesn't exist or has been removed.</p>
          <Button to="/programs" variant="primary">View All Programs</Button>
        </div>
      </Section>
    );
  }
  
  // Get category title from course data
  const getCategoryTitle = (categoryId) => {
    const category = COURSES_DATA[categoryId];
    return category ? category.title : categoryId.charAt(0).toUpperCase() + categoryId.slice(1);
  };
  
  return (
    <>
      <Helmet>
        <title>{program.title} - Afrovate Tech Hub</title>
        <meta name="description" content={program.shortDesc} />
      </Helmet>
      
      {/* Hero Banner */}
      <section 
        className="relative pt-32 pb-16 overflow-hidden"
        style={{
          backgroundImage: program.image 
            ? `url(${program.image})` 
            : `url(/images/programs/${program.id}/hero-bg.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Dark overlay for better text visibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-dark to-primary opacity-75"></div>
        
        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          {/* Breadcrumbs */}
          <div className="flex items-center mb-8 text-white text-sm">
            <Link to="/" className="flex items-center hover:text-secondary transition-colors">
              <FaHome className="mr-2" />
              Home
            </Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <Link to="/programs" className="hover:text-secondary transition-colors">Programs</Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <span className="text-secondary font-medium">{program.title}</span>
          </div>
          
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block bg-secondary text-white text-sm font-medium rounded-full px-3 py-1 mb-4">
                {getCategoryTitle(program.category).toUpperCase()}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {program.title}
              </h1>
              <p className="text-xl text-white text-opacity-90 mb-6">
                {program.shortDesc}
              </p>
              
              <div className="flex flex-wrap gap-6 text-white">
                <div className="flex items-center">
                  <FaCalendarAlt className="mr-2 text-secondary" />
                  <span>Next Cohort: {program.nextCohortStart}</span>
                </div>
                <div className="flex items-center">
                  <FaClock className="mr-2 text-secondary" />
                  <span>{program.duration}</span>
                </div>
                <div className="flex items-center">
                  <FaUsers className="mr-2 text-secondary" />
                  <span>Maximum 20 Students</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Navigation Tabs */}
      {/* <div className="sticky top-16 z-20 bg-white shadow-md">
        <div className="container mx-auto px-4">
          <div className="flex overflow-x-auto py-2 -mb-px font-medium text-sm md:text-base gap-1 md:gap-4">
            <button
              className={`whitespace-nowrap px-3 md:px-5 py-2 border-b-2 ${
                activeTab === 'overview'
                  ? 'border-primary text-primary font-semibold'
                  : 'border-transparent text-gray-500 hover:text-primary hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={`whitespace-nowrap px-3 md:px-5 py-2 border-b-2 ${
                activeTab === 'curriculum'
                  ? 'border-primary text-primary font-semibold'
                  : 'border-transparent text-gray-500 hover:text-primary hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('curriculum')}
            >
              Curriculum
            </button>
            <button
              className={`whitespace-nowrap px-3 md:px-5 py-2 border-b-2 ${
                activeTab === 'instructors'
                  ? 'border-primary text-primary font-semibold'
                  : 'border-transparent text-gray-500 hover:text-primary hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('instructors')}
            >
              Instructors
            </button>
            <button
              className={`whitespace-nowrap px-3 md:px-5 py-2 border-b-2 ${
                activeTab === 'faq'
                  ? 'border-primary text-primary font-semibold'
                  : 'border-transparent text-gray-500 hover:text-primary hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('faq')}
            >
              FAQ
            </button>
          </div>
        </div>
      </div> */}
      
      {/* Main Content Section */}
      <Section background="white" padding="lg">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Overview Tab Content */}
            {activeTab === 'overview' && (
              <div>
                <Heading level={2} className="mb-6">Program Overview</Heading>
                
                <div className="prose prose-lg max-w-none mb-8">
                  {program.overview ? (
                    <p>{program.overview}</p>
                  ) : (
                    <>
                      <p>
                        The {program.title} program is designed to equip participants with marketable digital skills
                        that are in high demand across Sierra Leone and beyond. This training focuses on practical,
                        hands-on experience that will prepare you for real-world challenges.
                      </p>
                      
                      <p>
                        Upon completion of this program, you will have developed a comprehensive portfolio showcasing
                        your projects and abilities, which will significantly enhance your employment prospects and
                        entrepreneurial capabilities.
                      </p>
                    </>
                  )}
                </div>
                
                <div className="mb-10">
                  <Heading level={3} className="mb-4">Key Features</Heading>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {program.features.map((feature, index) => (
                      <motion.div 
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1, duration: 0.5 }}
                        className="flex items-start"
                      >
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-1">
                          <FaCheckCircle className="text-green-600 text-sm" />
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
                
                {program.aims && program.aims.length > 0 && (
                  <div className="mb-10">
                    <Heading level={3} className="mb-4">Aims of the Training</Heading>
                    <ul className="space-y-3">
                      {program.aims.map((aim, index) => (
                        <li key={index} className="flex items-start">
                          <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                            <FaCheckCircle className="text-blue-600 text-sm" />
                          </div>
                          <span className="text-gray-700">{aim}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {program.benefits && program.benefits.length > 0 && (
                  <div className="mb-10">
                    <Heading level={3} className="mb-6">Benefits of the Training</Heading>
                    <div className="bg-gray-50 rounded-lg p-6 shadow-sm">
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {program.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-start">
                            <span className="h-7 w-7 rounded-full bg-primary-light bg-opacity-20 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                              <span className="text-primary font-bold text-sm">{index + 1}</span>
                            </span>
                            <span className="text-gray-700">{benefit.desc || benefit.title}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                
                <div className="mb-10">
                  <Heading level={3} className="mb-6">What You'll Learn</Heading>
                  <div className="bg-gray-50 rounded-lg p-6 shadow-sm">
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {(program.learn || program.aims || []).map((item, index) => (
                        <li key={index} className="flex items-start">
                          <span className="h-7 w-7 rounded-full bg-primary-light bg-opacity-20 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                            <span className="text-primary font-bold text-sm">{index + 1}</span>
                          </span>
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div className="mb-10">
                  <Heading level={3} className="mb-4">Requirements</Heading>
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
                    <ul className="space-y-3">
                      {(program.prerequisites || []).map((requirement, index) => (
                        <li key={index} className="flex items-start">
                          <FaCheckCircle className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
                          <span>{requirement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
              </div>
            )}
            
            {/* Curriculum Tab Content */}
            {activeTab === 'curriculum' && (
              <div>
                <Heading level={2} className="mb-6">Program Curriculum</Heading>
                
                <div className="prose prose-lg max-w-none mb-8">
                  <p>
                    Our curriculum is designed with a practical focus, ensuring you get hands-on experience
                    with the tools and techniques most valued by employers. The program is structured into
                    modules that build progressively on your skills and knowledge.
                  </p>
                </div>
                
                <div className="space-y-6 mb-8">
                  {/* If we have week-by-week curriculum from coursesdata.js */}
                  {Array.isArray(program.curriculum) && program.curriculum[0]?.week ? (
                    program.curriculum.map((week, index) => (
                      <div 
                        key={`week-${week.week}`}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <button
                          className="w-full flex justify-between items-center p-5 bg-gray-50 text-left"
                          onClick={() => toggleAccordion(`week-${week.week}`)}
                        >
                          <div className="flex items-center">
                            <span className="h-8 w-8 rounded-full bg-primary text-white flex items-center justify-center mr-3">
                              {week.week}
                            </span>
                            <div>
                              <h5 className="font-bold text-primary-dark">{week.title}</h5>
                            </div>
                          </div>
                          <span className="text-primary">
                            {openAccordion === `week-${week.week}` ? <FaAngleUp /> : <FaAngleDown />}
                          </span>
                        </button>
                        
                        {openAccordion === `week-${week.week}` && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="p-5 border-t border-gray-200"
                          >
                            <ul className="space-y-2">
                              {week.topics.map((topic, i) => (
                                <li key={i} className="flex items-start">
                                  <div className="h-5 w-5 rounded-full bg-green-100 flex-shrink-0 flex items-center justify-center mr-3 mt-1">
                                    <FaCheckCircle className="text-green-600 text-xs" />
                                  </div>
                                  <span className="text-gray-700">{topic}</span>
                                </li>
                              ))}
                            </ul>
                          </motion.div>
                        )}
                      </div>
                    ))
                  ) : (
                    // Otherwise use the module-based curriculum
                    program.curriculum.map((module, index) => (
                      <div 
                        key={module.id}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <button
                          className="w-full flex justify-between items-center p-5 bg-gray-50 text-left"
                          onClick={() => toggleAccordion(module.id)}
                        >
                          <div className="flex items-center">
                            <span className="h-8 w-8 rounded-full bg-primary text-white flex items-center justify-center mr-3">
                              {index + 1}
                            </span>
                            <div>
                              <h5 className="font-bold text-primary-dark">{module.title}</h5>
                              <p className="text-gray-600 text-sm">{module.duration}</p>
                            </div>
                          </div>
                          <span className="text-primary">
                            {openAccordion === module.id ? <FaAngleUp /> : <FaAngleDown />}
                          </span>
                        </button>
                        
                        {openAccordion === module.id && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="p-5 border-t border-gray-200 bg-gray-50"
                          >
                            <ul className="space-y-2">
                              {module.lessons.map((lesson, i) => (
                                <li key={i} className="flex items-start">
                                  <div className="h-5 w-5 rounded-full bg-green-100 flex-shrink-0 flex items-center justify-center mr-3 mt-1">
                                    <FaCheckCircle className="text-green-600 text-xs" />
                                  </div>
                                  <span className="text-gray-700">{lesson}</span>
                                </li>
                              ))}
                            </ul>
                          </motion.div>
                        )}
                      </div>
                    ))
                  )}
                </div>
                
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="font-bold text-primary-dark mb-4">Learning Outcomes</h3>
                  <p className="text-gray-700 mb-4">
                    By the end of this program, you will be able to:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <FaCheckCircle className="text-green-600 mt-1 mr-3 flex-shrink-0" />
                      <span>Confidently use industry-standard software for professional projects</span>
                    </li>
                    <li className="flex items-start">
                      <FaCheckCircle className="text-green-600 mt-1 mr-3 flex-shrink-0" />
                      <span>Create comprehensive digital designs from concept to final output</span>
                    </li>
                    <li className="flex items-start">
                      <FaCheckCircle className="text-green-600 mt-1 mr-3 flex-shrink-0" />
                      <span>Collaborate effectively with teams on complex projects</span>
                    </li>
                    <li className="flex items-start">
                      <FaCheckCircle className="text-green-600 mt-1 mr-3 flex-shrink-0" />
                      <span>Present your work professionally to clients and employers</span>
                    </li>
                    <li className="flex items-start">
                      <FaCheckCircle className="text-green-600 mt-1 mr-3 flex-shrink-0" />
                      <span>Apply your skills to solve real-world challenges in your field</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
            
            {/* Instructors Tab Content */}
            {activeTab === 'instructors' && (
              <div>
                <Heading level={2} className="mb-6">Program Instructors</Heading>
                
                <div className="prose prose-lg max-w-none mb-8">
                  <p>
                    Our instructors are experienced professionals who bring real-world knowledge and 
                    industry experience to the classroom. They are dedicated to your success and provide 
                    mentorship beyond the technical curriculum.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  {program.instructors.map((instructor, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100"
                    >
                      <div className="h-64 overflow-hidden">
                        <img
                          src={instructor.image}
                          alt={instructor.name}
                          className="w-full h-full object-cover transition-transform duration-500 transform hover:scale-110"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = instructor.fallbackImage;
                          }}
                        />
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-primary-dark">{instructor.name}</h3>
                        <p className="text-secondary font-medium mb-4">{instructor.title}</p>
                        <p className="text-gray-700">
                          With extensive experience in both teaching and industry practice, our lead instructor
                          brings practical knowledge and insights to the classroom, ensuring students learn
                          relevant skills that meet current industry demands.
                        </p>
                      </div>
                    </motion.div>
                  ))}
                  
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100"
                  >
                    <div className="h-64 overflow-hidden">
                      <img
                        src="/images/team/monica_musa.jpg"
                        alt="Monica Musa"
                        className="w-full h-full object-cover transition-transform duration-500 transform hover:scale-110"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://placehold.co/300x300/1a237e/ffffff?text=MM";
                        }}
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-primary-dark">Monica Musa</h3>
                      <p className="text-secondary font-medium mb-4">Assistant Instructor</p>
                      <p className="text-gray-700">
                        Monica supports our training program with her expertise in civil engineering and practical
                        field experience. As a former Afrovate Tech Hub trainee herself, she understands the learning journey
                        and provides valuable guidance to students.
                      </p>
                    </div>
                  </motion.div>
                </div>
                
                <div className="bg-primary-dark text-white rounded-lg p-8">
                  <Heading level={3} color="white" className="mb-4">
                    Our Teaching Philosophy
                  </Heading>
                  <p className="mb-6 text-white text-opacity-90">
                    At Afrovate Tech Hub, we believe in learning by doing. Our instructors follow these key principles:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex">
                      <div className="h-10 w-10 rounded-full bg-white text-primary flex items-center justify-center mr-4 flex-shrink-0">
                        1
                      </div>
                      <div>
                        <h4 className="font-bold text-white mb-2">Practical Over Theoretical</h4>
                        <p className="text-white text-opacity-80">
                          We emphasize hands-on practice over lengthy theoretical lectures.
                        </p>
                      </div>
                    </div>
                    <div className="flex">
                      <div className="h-10 w-10 rounded-full bg-white text-primary flex items-center justify-center mr-4 flex-shrink-0">
                        2
                      </div>
                      <div>
                        <h4 className="font-bold text-white mb-2">Peer-to-Peer Learning</h4>
                        <p className="text-white text-opacity-80">
                          We create an environment where students learn from each other as well as instructors.
                        </p>
                      </div>
                    </div>
                    <div className="flex">
                      <div className="h-10 w-10 rounded-full bg-white text-primary flex items-center justify-center mr-4 flex-shrink-0">
                        3
                      </div>
                      <div>
                        <h4 className="font-bold text-white mb-2">Real-World Projects</h4>
                        <p className="text-white text-opacity-80">
                          Assignments are based on actual industry scenarios and challenges.
                        </p>
                      </div>
                    </div>
                    <div className="flex">
                      <div className="h-10 w-10 rounded-full bg-white text-primary flex items-center justify-center mr-4 flex-shrink-0">
                        4
                      </div>
                      <div>
                        <h4 className="font-bold text-white mb-2">Ongoing Mentorship</h4>
                        <p className="text-white text-opacity-80">
                          Support continues beyond graduation through our alumni network.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* FAQ Tab Content */}
            {activeTab === 'faq' && (
              <div>
                <Heading level={2} className="mb-6">Frequently Asked Questions</Heading>
                
                <div className="prose prose-lg max-w-none mb-8">
                  <p>
                    Find answers to common questions about our {program.title} program. If you
                    don't see your question answered here, please feel free to contact us directly.
                  </p>
                </div>
                
                <div className="space-y-4 mb-8">
                  {program.faq.map((item) => (
                    <div 
                      key={item.id}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      <button
                        className="w-full flex justify-between items-center p-5 bg-white text-left"
                        onClick={() => toggleAccordion(item.id)}
                      >
                        <h5 className="font-bold text-primary-dark pr-8">{item.question}</h5>
                        <span className="text-primary flex-shrink-0">
                          {openAccordion === item.id ? <FaAngleUp /> : <FaAngleDown />}
                        </span>
                      </button>
                      
                      {openAccordion === item.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="p-5 border-t border-gray-200 bg-gray-50"
                        >
                          <p className="text-gray-700">{item.answer}</p>
                        </motion.div>
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
                  <h3 className="font-bold text-primary-dark mb-4 flex items-center">
                    <FaUsers className="mr-2 text-blue-600" />
                    Still Have Questions?
                  </h3>
                  <p className="text-gray-700 mb-4">
                    We're here to help! Reach out to us through any of these channels:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Button 
                      variant="primary" 
                      to="/contact" 
                      className="w-full"
                    >
                      Contact Us
                    </Button>
                    <Button 
                      variant="outline-primary" 
                      href="mailto:info@leotechsl.com" 
                      className="w-full"
                    >
                      Email: info@leotechsl.com
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-32">
              {/* Program Details Card */}
              <ProgramDetails program={program} />
              
              {/* Related Programs */}
              {program.relatedPrograms && program.relatedPrograms.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="font-bold text-primary-dark mb-4">Related Programs</h3>
                  <div className="space-y-4">
                    {program.relatedPrograms.map((relatedProgram) => (
                      <Link 
                        key={relatedProgram.id}
                        to={`/programs/${relatedProgram.id}`}
                        className="block bg-white p-4 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all duration-300"
                      >
                        <h4 className="font-bold text-primary-dark mb-2">{relatedProgram.title}</h4>
                        <p className="text-gray-600 text-sm mb-2">
                          {relatedProgram.shortDesc.substring(0, 100)}
                          {relatedProgram.shortDesc.length > 100 ? '...' : ''}
                        </p>
                        <div className="text-primary font-medium flex items-center text-sm">
                          Learn More <FaArrowRight className="ml-1" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Display tracks if available */}
              {program.tracks && program.tracks.length > 0 && (
                <div className="mt-6 bg-blue-50 rounded-lg p-6 border border-blue-100">
                  <h3 className="font-bold text-primary-dark mb-4">Program Tracks</h3>
                  <ul className="space-y-2">
                    {program.tracks.map((track, index) => (
                      <li key={index} className="flex items-start">
                        <div className="h-5 w-5 rounded-full bg-blue-100 flex-shrink-0 flex items-center justify-center mr-3 mt-1">
                          <FaCheckCircle className="text-blue-600 text-xs" />
                        </div>
                        <span className="text-gray-700">{track}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Display subprograms if available */}
              {program.subprograms && program.subprograms.length > 0 && (
                <div className="mt-6 bg-green-50 rounded-lg p-6 border border-green-100">
                  <h3 className="font-bold text-primary-dark mb-4">Included Subprograms</h3>
                  <ul className="space-y-2">
                    {program.subprograms.map((subprogram, index) => (
                      <li key={index} className="flex items-start">
                        <div className="h-5 w-5 rounded-full bg-green-100 flex-shrink-0 flex items-center justify-center mr-3 mt-1">
                          <FaCheckCircle className="text-green-600 text-xs" />
                        </div>
                        <span className="text-gray-700">{subprogram}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </Section>
      
      {/* Call to Action */}
      <Section background="primary-dark" padding="lg">
        <div className="text-center max-w-3xl mx-auto">
          <Heading level={2} color="white" align="center" className="mb-6">
            Ready to Transform Your Skills?
          </Heading>
          <p className="text-xl text-white text-opacity-90 mb-10">
            Join our next cohort and take the first step toward building a career with marketable digital skills.
            Applications are open now with limited seats available.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Button 
              variant="secondary" 
              size="lg" 
              to="/apply"
              icon={<FaArrowRight />}
              iconPosition="right"
            >
              Apply Now
            </Button>
            <Button 
              variant="light" 
              size="lg" 
              to="/programs"
              icon={<FaArrowLeft />}
              iconPosition="left"
            >
              Back to Programs
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
};

export default ProgramDetailPage;
